/**
 * Каркас экрана: шапка + прокручиваемое содержимое.
 *
 * §3.4 п. 4: прокручивается только содержимое; шапка, нижняя панель
 * и левая навигация имеют собственные слои и не перекрывают фокус.
 */
import { useNavigate, useRouter } from '@tanstack/react-router'
import { useRef, type ReactNode, type RefObject, type UIEvent } from 'react'

import { Icon, cx } from '@shared/ui'

import { useAppStore } from '../model/app-store'
import { hasInAppHistory } from '../model/navigation-direction'
import { SCREEN_SCROLL_ATTR } from '../model/screen-scroll'
import { ScreenFrame, type ScreenFrameHandle } from './ScreenFrame'
import styles from './Screen.module.css'

/** Родительский экран для «назад» по deep link — когда предыдущей записи приложения нет. */
function parentPath(pathname: string, authenticated: boolean): string {
  if (pathname.startsWith('/settings/')) return '/settings'
  // Профиль живёт внутри «Настроек» (шапка S-01), его подэкраны — внутри профиля
  if (pathname === '/me') return '/settings'
  if (pathname.startsWith('/me/')) return '/me'
  if (pathname.startsWith('/users/') || pathname.startsWith('/dvizh/')) return '/dvizh'
  if (pathname.startsWith('/chats/') || pathname.startsWith('/@')) return '/chats'
  // C-38: выбор собеседника для секретного чата открывается из «Чатов» (изменение v1.13).
  if (pathname.startsWith('/secret')) return '/chats'
  if (pathname === '/search') return '/contacts'
  if (pathname.startsWith('/notifications/')) return '/notifications'
  if (pathname.startsWith('/reports/') || pathname.startsWith('/appeals/')) return '/settings'
  if (pathname.startsWith('/legal/')) return authenticated ? '/settings/legal' : '/'
  if (pathname === '/support/cant-login') return authenticated ? '/settings/support' : '/auth'
  return authenticated ? '/dvizh' : '/'
}

interface ScreenProps {
  title?: ReactNode
  subtitle?: ReactNode
  /** Показать кнопку «назад». `true` — вернуться в истории. */
  back?: boolean | (() => void)
  actions?: ReactNode
  /** Полоса под шапкой: фильтры, вкладки, поиск. */
  subbar?: ReactNode
  /** Элемент в самой шапке вместо заголовка (заголовок остаётся для скринридера). */
  headerContent?: ReactNode
  children: ReactNode
  /** Убрать поля содержимого — для списков во всю ширину. */
  flush?: boolean
  /** Ограничить содержимое 720 px (U-*, S-*, N-01). */
  narrow?: boolean
  /** Шапка на сплошном фоне — для экранов без списка под ней. */
  plainHeader?: boolean
  className?: string
  contentClassName?: string
  /** Область прокрутки — для восстановления позиции при возврате на экран. */
  contentRef?: RefObject<HTMLDivElement>
  onContentScroll?: (event: UIEvent<HTMLDivElement>) => void
}

export function Screen({
  title,
  subtitle,
  back,
  actions,
  subbar,
  headerContent,
  children,
  flush = false,
  narrow = false,
  plainHeader = false,
  className,
  contentClassName,
  contentRef,
  onContentScroll,
}: ScreenProps): React.ReactElement {
  const router = useRouter()
  const navigate = useNavigate()
  const frameRef = useRef<ScreenFrameHandle>(null)

  /** Сам переход «назад» — его же вызывает жест от края после своей анимации. */
  const navigateBack =
    typeof back === 'function'
      ? back
      : () => {
          if (hasInAppHistory()) {
            router.history.back()
            return
          }
          const pathname = router.state.location.pathname
          void navigate({ to: parentPath(pathname, useAppStore.getState().phase === 'ready'), replace: true })
        }

  /** Кнопка «назад»: сначала экран уезжает, потом происходит переход (ScreenFrame). */
  const goBack = (): void => {
    if (frameRef.current) frameRef.current.pop(navigateBack)
    else navigateBack()
  }

  const hasHeader = title !== undefined || back || actions || headerContent

  /*
   * Вложенный экран (с «назад») входит с той стороны, куда шёл переход, и
   * уходит жестом от края; корневые разделы и перезагрузка не анимируются.
   */
  return (
    <ScreenFrame
      ref={frameRef}
      nested={Boolean(back)}
      onBack={back ? navigateBack : undefined}
      className={cx(styles['screen'], className)}
    >
      {hasHeader ? (
        <header className={cx(styles['header'], plainHeader && styles['headerPlain'])}>
          {back ? (
            <button type="button" className={styles['back']} onClick={goBack} aria-label="Назад">
              <Icon name="back" size={22} />
            </button>
          ) : null}

          <div
            className={cx(styles['titleBlock'], Boolean(headerContent) && styles['titleBlockWithContent'])}
          >
            {title !== undefined ? (
              <h1 className={cx(styles['title'], Boolean(headerContent) && 'visually-hidden')}>{title}</h1>
            ) : null}
            {subtitle && !headerContent ? <div className={styles['subtitle']}>{subtitle}</div> : null}
            {headerContent ? <div className={styles['headerContent']}>{headerContent}</div> : null}
          </div>

          {actions ? <div className={styles['actions']}>{actions}</div> : null}
        </header>
      ) : null}

      {subbar ? <div className={styles['subbar']}>{subbar}</div> : null}

      <div
        ref={contentRef}
        className={cx(styles['content'], !flush && styles['contentPadded'], contentClassName)}
        onScroll={onContentScroll}
        /* Позиция возвращается при возврате на экран (app/model/screen-scroll.ts) */
        {...{ [SCREEN_SCROLL_ATTR]: true }}
      >
        {/* Ограничение ширины — на вложенном блоке, чтобы полоса прокрутки оставалась у края окна */}
        {narrow ? <div className={styles['narrow']}>{children}</div> : children}
      </div>
    </ScreenFrame>
  )
}
